/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import isNil from 'lodash/isNil';
import isString from 'lodash/isString';

import CommunicatorNumber from '../../../communicator/number/communicator-number';
import CommunicatorUtility from '../../../communicator/utility/communicator-utility';
import DhtmlxRecFrequency from '../../../dhtmlx/dhtmlx-rec-frequency';

/**
 * Komunikátor obsahových úloh pro Ki-Wi Server
 */
class ContentTaskCommunicator {
  /**
   * @param {DhtmlxDateParser} dhtmlxDateParser
   * @param {DhtmlxRecTypeUtil} dhtmlxRecTypeUtil
   * @param {DhtmlxToIcalExporter} dhtmlxToIcalExporter
   * @param {ContentTaskPlanUidGenerator} uidGenerator
   */
  constructor(
    dhtmlxDateParser,
    dhtmlxRecTypeUtil,
    dhtmlxToIcalExporter,
    uidGenerator
  ) {
    /**
     * Formát, ve kterém jsou v obsahu zařízení uloženy datumy.
     * Tuto informaci by šlo předávat v konstruktoru, ale zatím je to zakázáno,
     * kvůli chybějícímu `DhtmlxDateStringifier` viz.
     * {@link ContentTaskCommunicator#infinityEndDate}.
     * @private
     * @type {string}
     */
    this.dateFormat = '%Y-%m-%dT%H:%i:%s';
    this.dhtmlxDateParser = dhtmlxDateParser;
    this.dhtmlxRecTypeUtil = dhtmlxRecTypeUtil;
    this.dhtmlxToIcalExporter = dhtmlxToIcalExporter;
    /**
     * Konečné datum DHTMLX události, které se nastavuje v případě,
     * že se událost opakuje do nekonečna.
     * Přebírá se zde hodnota, která se používá v oficiálním DHTMLX kalendáři.
     * Není jasné proč originální implementace používá únor místo ledna.
     * Možná jde jen o bug, kdy v JS Date jsou měsíce číslovány od 0,
     * takže číslo 1 představuje únor.
     * Zásadním problémem je, že tato hodnota musí vycházet z dateFormat.
     * To by při dokonalém ošetření vyžadovalo implementaci nové třídy
     * `DhtmlxDateStringifier`, která zatím není.
     * Prozatím tedy ponecháno zde jako konstanta společně s formátem datumů.
     * @private
     * @type {string}
     */
    this.infinityEndDate = '9999-02-01T00:00:00';
    this.uidGenerator = uidGenerator;
  }

  /**
   * Vygeneruje pseudo-unikátní identifikátor iCalendar události.
   * Identifikátor je unikátní v rámci Player aplikace,
   * což by mělo požadavkům specifikace vyhovět.
   * @param {DhtmlxEvent} dhtmlxEvent
   * @private
   * @returns {string}
   */
  generateUid(dhtmlxEvent) {
    return this.uidGenerator.generate(dhtmlxEvent.id);
  }

  /**
   * Zpracuje obsahovou úlohu.
   * @param {ContentTaskCommunicatorJson} json
   * @param {Store} store
   * @returns {ContentTask}
   */
  parse(json, store) {
    const { id, ref } = json;
    return store.inject('contentTask', {
      id,
      layoutId: CommunicatorUtility.parseReference(ref, 'layout'),
      mediumId: CommunicatorUtility.parseReference(ref, 'media'),
      planIcal: this.parseIcal(id, json),
      playlistId: CommunicatorUtility.parseReference(ref, 'playlist'),
      priority: CommunicatorNumber.fromNumber(json.priority, {
        required: true
      }),
      target: CommunicatorUtility.parseString(json.target, { required: true })
    });
  }

  /**
   * Načte DHTMLX Scheduler událost z formátu obsahových úloh.
   * @param {string} dateFormat Formát datumů v DHTMLX události
   * @param {DsId} id Identifikátor úlohy
   * @param {ContentTaskCommunicatorJson} json
   * @returns {DhtmlxEvent}
   */
  parseDhtmlxEvent(dateFormat, id, json) {
    const {
      endIteration,
      endTime,
      rec_type: recType,
      repeat,
      startTime
    } = json;
    if (!isString(endTime) || endTime.length === 0) {
      throw new TypeError(`Invalid end time "${endTime}".`);
    }
    if (!isString(startTime) || startTime.length === 0) {
      throw new TypeError(`Invalid start time "${startTime}".`);
    }
    // Pokud má událost definovaný DHTMLX rec_type,
    // tak se primárně parsuje dle něj, jako opakovaná událost.
    if (isString(recType) && recType.length !== 0) {
      // V tomto případě musí být definovaný i konec iterace.
      if (!isString(endIteration) || endIteration.length === 0) {
        throw new TypeError(`Invalid iteration end time "${endIteration}".`);
      }
      return this.parseRecurringEvent(
        dateFormat,
        endIteration,
        endTime,
        id,
        recType,
        startTime
      );
    }
    // Pokud má událost definovaný legacy repeat příznak,
    // tak se parsuje dle něj, jako opakovaná událost.
    if (isString(repeat) && repeat.length !== 0) {
      // V tomto případě musí být definovaný i konec iterace.
      if (!isString(endIteration) || endIteration.length === 0) {
        throw new TypeError(`Invalid iteration end time "${endIteration}".`);
      }
      return this.parseLegacyRecurringEvent(
        dateFormat,
        endIteration,
        endTime,
        id,
        repeat,
        startTime
      );
    }
    // Pokud událost nemá žádnou z položek pro vyjádření opakování,
    // načte se jako událost bez opakování.
    return this.parseOneTimeEvent(endTime, id, startTime);
  }

  /**
   * Načte identifikátor události.
   * Jelikož v obsahu chybí identifikátor DHTMLX události a každá úloha má jen
   * jednu událost, použije se identifikátor úlohy.
   * Pouze se převede na `string`, jak to vyžaduje DHTMLX formát.
   * @param {DsId} id Identifikátor úlohy
   * @private
   * @returns {string}
   */
  parseEventId(id) {
    return String(id);
  }

  /**
   * Načte dobu trvání události.
   * @param {Date} endDate Datum `endTime` z obsahu zařízení
   * @param {Date} startDate Datum `startTime` z obsahu zařízení
   * @private
   * @returns {string}
   */
  parseEventLength(endDate, startDate) {
    // Doba trvání události se určí jako rozdíl mezi časem jejího konce
    // a začátku.
    const miliseconds = endDate.getTime() - startDate.getTime();
    // Doba musí být konečná, zde se především ošetřuje možnost,
    // že by některé z datumů neblo platné.
    if (!Number.isFinite(miliseconds)) {
      throw new TypeError(`Invalid event length "${miliseconds}".`);
    }
    // Převod výsledku na vteřiny a do řetězce, jak to vyžaduje DHTMLX.
    const seconds = Math.floor(miliseconds / 1000);
    return String(seconds);
  }

  /**
   * Načte z obsahové úlohy kalendář s médiaplánem, pokud stapa nějaký obsahuje.
   * @param {DsId} id Identifikátor úlohy
   * @param {ContentTaskCommunicatorJson} json
   * @private
   * @returns {string}
   */
  parseIcal(id, json) {
    const { dateFormat } = this;
    const dhtmlxEvent = this.parseDhtmlxEvent(dateFormat, id, json);
    const dhtmlxEvents = [dhtmlxEvent];
    const ical = this.dhtmlxToIcalExporter.export(dhtmlxEvents, {
      dateFormat,
      generateUid: (...args) => this.generateUid(...args)
    });
    if (isNil(ical)) {
      throw new Error(`Unable to crate iCalendar from "${id}".`);
    }
    return ical;
  }

  /**
   * Načte parametry opakování DHTMLX události.
   * @param {string} repeat Typ opakování `repeat` z obsahu zařízení
   * @param {Date} startDate Datum `startTime` z obsahu zařízení
   * @private
   * @returns {string}
   */
  parseLegacyRecType(repeat, startDate) {
    // TODO Zavést číselník opakování ze serveru
    const { dhtmlxRecTypeUtil } = this;
    switch (repeat) {
      case 'Daily':
        // Denní opakování
        // Podporováno je pouze opakování každý den do určitého datumu
        return dhtmlxRecTypeUtil.stringify({ type: DhtmlxRecFrequency.Day });
      case 'Monthly':
        // Měsíční opakování
        // Podporováno je pouze opakování každý měsíc do určitého datumu
        return dhtmlxRecTypeUtil.stringify({ type: DhtmlxRecFrequency.Month });
      case 'Weekdays':
        // Opakování v pracovní dny
        // Z pohledu DHTMLX jde o týdenní opakování pro dny od pondělí do pátku.
        // Podporováno je pouze opakování každý týden do určitého datumu.
        return dhtmlxRecTypeUtil.stringify({
          days: [1, 2, 3, 4, 5],
          type: DhtmlxRecFrequency.Week
        });
      case 'Weekly':
        // Týdenní opakování
        // Podporováno je pouze opakování v jeden vybraný den každý týden.
        // Vybraný den v týdnu určuje den v týdnu počátečního datumu.
        return dhtmlxRecTypeUtil.stringify({
          days: [startDate.getDay()],
          type: DhtmlxRecFrequency.Week
        });
      default:
        throw new RangeError(`Unknown repeat "${repeat}".`);
    }
  }

  /**
   * Načte DHTMLX Scheduler událost s opakováním z formátu obsahových úloh.
   * Používá se starý, nestandardizovaný formát vycházející z DHTMLX.
   * @param {string} dateFormat Formát datumů v DHTMLX události
   * @param {string} endIteration Datum `endIteration` z obsahu zařízení
   * @param {string} endTime Datum `endTime` z obsahu zařízení
   * @param {DsId} id Identifikátor úlohy
   * @param {string} repeat Typ opakování `repeat` z obsahu zařízení
   * @param {string} startTime Datum `startTime` z obsahu zařízení
   * @private
   * @returns {DhtmlxEvent}
   */
  parseLegacyRecurringEvent(
    dateFormat,
    endIteration,
    endTime,
    id,
    repeat,
    startTime
  ) {
    const { dhtmlxDateParser } = this;
    const endDate = dhtmlxDateParser.parse(endTime, dateFormat);
    const startDate = dhtmlxDateParser.parse(startTime, dateFormat);
    return {
      // U opakovaných událostí je v end_date uložen konec opakování,
      // nikoliv konec prvního výskytu události.
      end_date: endIteration,
      // Délka události je u opakovaných nutná, protože nahrazuje informace
      // uložené v end_date u jednorázových událostí.
      event_length: this.parseEventLength(endDate, startDate),
      // Výjimky z opakování obsahových úloh se nepoužívají.
      // Prázdná hodnota znamená, že nejde o výjimku (nemá rodičovskou událost).
      event_pid: '',
      // Identifikátor události
      id: this.parseEventId(id),
      // Nastavení opakování události.
      rec_type: this.parseLegacyRecType(repeat, startDate).toString(),
      // Začátek opakování události, který se nemusí nutně shodovat se začátkem
      // prvního výskytu události.
      start_date: startTime,
      // Popis události se zahodí, není potřeba.
      text: ''
    };
  }

  /**
   * Načte DHTMLX Scheduler událost bez opakování z formátu obsahových úloh.
   * @param {string} endTime Datum `endTime` z obsahu zařízení
   * @param {DsId} id Identifikátor úlohy
   * @param {string} startTime Datum `startTime` z obsahu zařízení
   * @private
   * @returns {DhtmlxEvent}
   */
  parseOneTimeEvent(endTime, id, startTime) {
    // TODO Nevadí, že se nekontroluje validita endTime a startTime?
    return {
      // Konec události
      end_date: endTime,
      // Délka události se ponechá prázdná,
      // což DHTMLX formát dovoluje u událostí bez opakování.
      event_length: '',
      // Výjimky z opakování obsahových úloh se nepoužívají.
      // Prázdná hodnota znamená, že nejde o výjimku (nemá rodičovskou událost).
      event_pid: '',
      // Identifikátor události
      id: this.parseEventId(id),
      // Opakování obsahových úloh se nepoužívá.
      rec_type: '',
      // Začátek události
      start_date: startTime,
      // Popis události se zahodí, není potřeba.
      text: ''
    };
  }

  /**
   * Načte DHTMLX Scheduler událost s opakováním z formátu obsahových úloh.
   * Používá se starý, nestandardizovaný formát vycházející z DHTMLX
   * v kombinaci s položkou rec_type z DHTMLX standardu,
   * pomocí které se vyjadřuje opakování události.
   * @param {string} dateFormat Formát datumů v DHTMLX události
   * @param {string} endIteration Datum `endIteration` z obsahu zařízení
   * @param {string} endTime Datum `endTime` z obsahu zařízení
   * @param {DsId} id Identifikátor úlohy
   * @param {string} recType DHTMLX `rec_type` z obsahu zařízení
   * @param {string} startTime Datum `startTime` z obsahu zařízení
   * @private
   * @returns {DhtmlxEvent}
   */
  parseRecurringEvent(
    dateFormat,
    endIteration,
    endTime,
    id,
    recType,
    startTime
  ) {
    const { dhtmlxDateParser } = this;
    const endDate = dhtmlxDateParser.parse(endTime, dateFormat);
    const startDate = dhtmlxDateParser.parse(startTime, dateFormat);
    return {
      // U opakovaných událostí je v end_date uložen konec opakování,
      // nikoliv konec prvního výskytu události.
      end_date: this.parseRecurringEndDate(endIteration, recType),
      // Délka události je u opakovaných nutná, protože nahrazuje informace
      // uložené v end_date u jednorázových událostí.
      event_length: this.parseEventLength(endDate, startDate),
      // Výjimky z opakování obsahových úloh se nepoužívají.
      // Prázdná hodnota znamená, že nejde o výjimku (nemá rodičovskou událost).
      event_pid: '',
      // Identifikátor události
      id: this.parseEventId(id),
      // Nastavení opakování události.
      rec_type: recType,
      // Začátek opakování události, který se nemusí nutně shodovat se začátkem
      // prvního výskytu události.
      start_date: startTime,
      // Popis události se zahodí, není potřeba.
      text: ''
    };
  }

  /**
   * Načte datum konce události s opakováním.
   * @param {string} endIteration Datum `endIteration` z obsahu zařízení
   * @param {string} recType DHTMLX `rec_type` z obsahu zařízení
   * @private
   * @returns {DhtmlxEvent}
   */
  parseRecurringEndDate(endIteration, recType) {
    // U nekonečného opakování je hodnota end_date nevýznamná.
    // V tomto případě se používá specifická hodnota datumu, jiné aplikace
    // totiž mohou tuto hodnotu používat k detekci nekonečného opakování.
    // Je tomu tak i u oficiálního převodníku z DHTMLX do iCal napsaném v PHP.
    const recTypeOptions = this.dhtmlxRecTypeUtil.parse(recType);
    if (recTypeOptions.infinite) {
      return this.infinityEndDate;
    }
    // Pokud není opakování nekonečné, tak končí datumem, které se vrátí.
    return endIteration;
  }
}

export default ContentTaskCommunicator;
